import { makeActivateEntityUpdateInput } from '@/modules/entities/components/ConvertToActiveMenuItem/makeActivateEntityUpdateInput';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import { NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL } from '@/modules/liabilities/LiabilityModal/LiabilityModal.constants';
import {
  AugmentedCreateLiabilityInput,
  AugmentedUpdateEntityInput,
  EntityKind,
  EntityStage,
} from '@/types/schema';

import {
  LiabilitiesStepFormShape,
  LiabilitiesStepRowData,
} from './LiabilitiesStep.types';

/** Converts values that do not identify an entity to undefined. */
function resolveEntityId(id: string): string | undefined {
  return id === '' || id === NO_LIABILITY_SOURCE_RECIPIENT_VALUE_SENTINEL
    ? undefined
    : id;
}

function buildPaymentFields(row: LiabilitiesStepRowData) {
  if (!row._showExpandedPaymentDetails) return {};

  return {
    paymentKind: row.paymentKind,
    paymentFrequency: row.paymentFrequency,
    recurringPaymentAmount: row.paymentAmount ?? undefined,
    interestRate: row.interestRate ?? undefined,
    termStart: row.termStartDate ?? undefined,
    termEnd: row.termEndDate ?? undefined,
  };
}

export function mapFormDataToMutation(
  formData: LiabilitiesStepFormShape,
  householdId: string
): AugmentedCreateLiabilityInput[] {
  return Object.values(formData.liabilities)
    .filter((row) => row.shouldImport)
    .map((row) => ({
      create: {
        displayName: row.name,
        householdID: householdId,
        integrationLiabilityID: row.integrationLiabilityId,
        currentAmount: row.outstandingBalance ?? undefined,
        currentAmountAsOfDate: row.balanceAsOfDate ?? undefined,
        sourceEntityID: resolveEntityId(row.issuerId),
        recipientEntityID: resolveEntityId(row.recipientId),
        payFromEntityID: row.hasDifferentPaymentSource
          ? row.alternateSourceId || undefined
          : undefined,
        notes: row.notes || undefined,
        ...buildPaymentFields(row),
      },
    }));
}

export interface LiabilityActivationEntity {
  id: string;
  stage: EntityStage;
  kind: EntityKind;
  subtype: { id: string };
}

export function getSelectedLiabilityEntityUpdates(
  formData: LiabilitiesStepFormShape,
  entities: LiabilityActivationEntity[]
): AugmentedUpdateEntityInput[] {
  const selectedEntityIds = new Set(
    Object.values(formData.liabilities)
      .filter((row) => row.shouldImport)
      .flatMap((row) => [row.issuerId, row.recipientId])
  );

  return entities
    .filter(
      (entity) =>
        entity.stage === EntityStage.PreCreated &&
        selectedEntityIds.has(entity.id)
    )
    .map((entity) =>
      makeActivateEntityUpdateInput({
        entityId: entity.id,
        subtypeId: entity.subtype.id,
        entityType: getEntityTypeFromEntityKind(entity.kind),
      })
    );
}

export function hasSelectedLiabilities(
  liabilities: LiabilitiesStepFormShape['liabilities']
): boolean {
  return Object.values(liabilities).some((row) => row.shouldImport);
}
